import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";
import { apiErrorMessage } from "utilities/approvalResponse";

const adminBase = `${baseURL}/admin`;

const useAuditApprovals = () => {
  const token = useSelector((state) => state.token);
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  const [entities, setEntities] = useState([]);
  const [isLoadingEntities, setLoadingEntities] = useState(true);
  const [entitiesError, setEntitiesError] = useState(false);

  const [policies, setPolicies] = useState([]);
  const [isLoadingPolicies, setLoadingPolicies] = useState(true);
  const [policiesError, setPoliciesError] = useState(false);
  const [policyActionError, setPolicyActionError] = useState(null);
  const [isSubmittingPolicy, setSubmittingPolicy] = useState(false);

  const [requests, setRequests] = useState({ total: 0, results: [] });
  const [isLoadingRequests, setLoadingRequests] = useState(true);
  const [requestsError, setRequestsError] = useState(false);
  const [requestActionError, setRequestActionError] = useState(null);
  const [isSubmittingRequest, setSubmittingRequest] = useState(false);

  const [auditEvents, setAuditEvents] = useState({ total: 0, results: [] });
  const [isLoadingAuditEvents, setLoadingAuditEvents] = useState(true);
  const [auditEventsError, setAuditEventsError] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    const fetchEntities = async () => {
      setLoadingEntities(true);
      setEntitiesError(false);
      try {
        const response = await axios.get(`${adminBase}/audit/entities`);
        setEntities(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch {
        setEntitiesError(true);
      } finally {
        setLoadingEntities(false);
      }
    };
    if (token) fetchEntities();
  }, [token, refreshKey]);

  const fetchPolicies = useCallback(
    async (filters = {}) => {
      setLoadingPolicies(true);
      setPoliciesError(false);
      try {
        const params = new URLSearchParams();
        if (filters.entity_code) params.set("entity_code", filters.entity_code);
        if (filters.organization_id) params.set("organization_id", filters.organization_id);
        const qs = params.toString();
        const response = await axios.get(
          `${adminBase}/approvals/policies${qs ? `?${qs}` : ""}`
        );
        setPolicies(Array.isArray(response.data?.data) ? response.data.data : []);
        return { success: true };
      } catch (error) {
        setPoliciesError(true);
        return { success: false, message: apiErrorMessage(error, "Failed to load policies.") };
      } finally {
        setLoadingPolicies(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) fetchPolicies();
  }, [token, refreshKey, fetchPolicies]);

  const fetchRequests = useCallback(
    async (filters = {}) => {
      setLoadingRequests(true);
      setRequestsError(false);
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, value);
          }
        });
        const qs = params.toString();
        const response = await axios.get(
          `${adminBase}/approvals/requests${qs ? `?${qs}` : ""}`
        );
        const data = response.data?.data ?? {};
        setRequests({
          total: data.total ?? 0,
          results: Array.isArray(data.results) ? data.results : [],
        });
        return { success: true, data };
      } catch (error) {
        setRequestsError(true);
        return { success: false, message: apiErrorMessage(error, "Failed to load requests.") };
      } finally {
        setLoadingRequests(false);
      }
    },
    [token]
  );

  const fetchRequestDetail = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${adminBase}/approvals/requests/${id}`);
        return { success: true, data: response.data?.data };
      } catch (error) {
        return { success: false, message: apiErrorMessage(error, "Failed to load request.") };
      }
    },
    [token]
  );

  const fetchAuditEvents = useCallback(
    async (filters = {}) => {
      setLoadingAuditEvents(true);
      setAuditEventsError(false);
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, value);
          }
        });
        const qs = params.toString();
        const response = await axios.get(
          `${adminBase}/audit/events${qs ? `?${qs}` : ""}`
        );
        const data = response.data?.data ?? {};
        setAuditEvents({
          total: data.total ?? 0,
          results: Array.isArray(data.results) ? data.results : [],
        });
        return { success: true, data };
      } catch (error) {
        setAuditEventsError(true);
        return { success: false, message: apiErrorMessage(error, "Failed to load audit events.") };
      } finally {
        setLoadingAuditEvents(false);
      }
    },
    [token]
  );

  const fetchAuditEventDetail = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${adminBase}/audit/events/${id}`);
        return { success: true, data: response.data?.data };
      } catch (error) {
        return { success: false, message: apiErrorMessage(error, "Failed to load audit event.") };
      }
    },
    [token]
  );

  const savePolicy = async (payload, policyId = null) => {
    setSubmittingPolicy(true);
    setPolicyActionError(null);
    try {
      const response = policyId
        ? await axios.patch(`${adminBase}/approvals/policies/${policyId}`, payload)
        : await axios.post(`${adminBase}/approvals/policies`, payload);
      refresh();
      return { success: true, data: response.data?.data };
    } catch (error) {
      const message = apiErrorMessage(error, "Failed to save policy.");
      setPolicyActionError(message);
      return { success: false, message };
    } finally {
      setSubmittingPolicy(false);
    }
  };

  const deactivatePolicy = async (policyId) => {
    setSubmittingPolicy(true);
    setPolicyActionError(null);
    try {
      await axios.delete(`${adminBase}/approvals/policies/${policyId}`);
      refresh();
      return { success: true };
    } catch (error) {
      const message = apiErrorMessage(error, "Failed to deactivate policy.");
      setPolicyActionError(message);
      return { success: false, message };
    } finally {
      setSubmittingPolicy(false);
    }
  };

  const approveRequest = async (id, comment = "") => {
    setSubmittingRequest(true);
    setRequestActionError(null);
    try {
      const response = await axios.post(`${adminBase}/approvals/requests/${id}/approve`, {
        comment,
      });
      refresh();
      return { success: true, data: response.data?.data };
    } catch (error) {
      const message = apiErrorMessage(error, "Failed to approve request.");
      setRequestActionError(message);
      return { success: false, message };
    } finally {
      setSubmittingRequest(false);
    }
  };

  const rejectRequest = async (id, comment) => {
    setSubmittingRequest(true);
    setRequestActionError(null);
    try {
      const response = await axios.post(`${adminBase}/approvals/requests/${id}/reject`, {
        comment,
      });
      refresh();
      return { success: true, data: response.data?.data };
    } catch (error) {
      const message = apiErrorMessage(error, "Failed to reject request.");
      setRequestActionError(message);
      return { success: false, message };
    } finally {
      setSubmittingRequest(false);
    }
  };

  const applyApprovedRequest = async (request) => {
    const { entity_code, action, payload, id: approvalRequestId } = request;
    const endpoints = {
      landlord_payout: { CREATE: `${adminBase}/landlord_payments` },
      rental_payment_custom: { CREATE: `${baseURL}/tenants/payments/custom` },
    };
    const url = endpoints[entity_code]?.[action];
    if (!url) {
      return { success: false, message: "This request type cannot be applied from the inbox yet." };
    }
    setSubmittingRequest(true);
    setRequestActionError(null);
    try {
      const response = await axios.post(url, {
        ...payload,
        approval_request_id: approvalRequestId,
      });
      refresh();
      return { success: true, data: response.data };
    } catch (error) {
      const message = apiErrorMessage(error, "Failed to apply approved action.");
      setRequestActionError(message);
      return { success: false, message };
    } finally {
      setSubmittingRequest(false);
    }
  };

  return {
    entities,
    isLoadingEntities,
    entitiesError,
    policies,
    isLoadingPolicies,
    policiesError,
    policyActionError,
    isSubmittingPolicy,
    requests,
    isLoadingRequests,
    requestsError,
    requestActionError,
    isSubmittingRequest,
    auditEvents,
    isLoadingAuditEvents,
    auditEventsError,
    refresh,
    fetchPolicies,
    fetchRequests,
    fetchRequestDetail,
    fetchAuditEvents,
    fetchAuditEventDetail,
    savePolicy,
    deactivatePolicy,
    approveRequest,
    rejectRequest,
    applyApprovedRequest,
  };
};

const usePendingApprovalsForMe = ({ enabled = true, pollIntervalMs = 60000 } = {}) => {
  const token = useSelector((state) => state.token);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled || !token) {
      setPendingCount(0);
      setPendingItems([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(`${adminBase}/approvals/requests`, {
        params: { pending_for_me: true, limit: 20, offset: 0 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data?.data ?? {};
      const results = Array.isArray(data.results) ? data.results : [];
      setPendingCount(data.total ?? results.length);
      setPendingItems(results);
    } catch {
      setPendingCount(0);
      setPendingItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, token]);

  useEffect(() => {
    load();
    if (!enabled || !token) return undefined;

    const interval = setInterval(load, pollIntervalMs);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [load, enabled, token, pollIntervalMs]);

  return { pendingCount, pendingItems, isLoading, refetch: load };
};

export { useAuditApprovals, usePendingApprovalsForMe };
